// TYPES
import { useMemo, useState } from 'react';
import type { TAttributeValueArrayPair } from '@timeedit/types/lib/types';
import { Icon } from '@timeedit/ui-components';
import {
  Button,
  ConfigProvider,
  Input,
  Table,
} from '@timeedit/ui-components/antd';
import type {
  SortOrder,
  SorterResult,
} from '@timeedit/ui-components/antd/table/interface';
// i18N
import intl from '../../../i18n/intl';

type Props = {
  attributePairs: TAttributeValueArrayPair[];
  onChange: (attributePairs: TAttributeValueArrayPair[]) => void;
};

type Row = {
  id: number;
  attribute: string;
  value: string;
};

const language: any = intl.messages;

const AttributesParsingTable = ({ attributePairs = [], onChange }: Props) => {
  const rows = useMemo(
    () =>
      attributePairs.map<Row>((pair, idx) => ({
        id: idx,
        attribute: pair[0],
        value: pair[1],
      })),
    [attributePairs]
  );

  const { sortedRows, onTableChange } = useStaticSort(rows);

  const onAddRow = () => {
    onChange([...attributePairs, ['', '']]);
  };

  const onUpdateRow = (rowIdx: number, colIdx: number, value: string) => {
    const updatedRow: TAttributeValueArrayPair = [
      colIdx === 0 ? value : attributePairs[rowIdx][0],
      colIdx === 1 ? value : attributePairs[rowIdx][1],
    ];
    const updatedArr = [
      ...attributePairs.slice(0, rowIdx),
      updatedRow,
      ...attributePairs.slice(rowIdx + 1),
    ];
    onChange(updatedArr);
  };

  const onDeleteRow = (rowIdx: number) => {
    const updatedArr = [
      ...attributePairs.slice(0, rowIdx),
      ...attributePairs.slice(rowIdx + 1),
    ];
    onChange(updatedArr);
  };

  return (
    // borderColor matches the antd default input border (#d9d9d9); the `bordered`
    // table otherwise uses the lighter colorBorderSecondary. Revisit under v7 theme.
    <ConfigProvider
      theme={{ components: { Table: { borderColor: '#d9d9d9' } } }}
    >
      <Table
        bordered
        size="small"
        dataSource={sortedRows}
        onChange={onTableChange}
        columns={[
          {
            title: language.attributes,
            dataIndex: 'attribute',
            key: 'attribute',
            width: 260,
            sorter: true,
            render: (val, record) => (
              <Input
                size="small"
                value={val}
                onChange={(e) => onUpdateRow(record.id, 0, e.target.value)}
              />
            ),
          },
          {
            title: language.values,
            dataIndex: 'value',
            key: 'value',
            width: 260,
            sorter: true,
            render: (val, record) => (
              <Input
                size="small"
                value={val}
                onChange={(e) => onUpdateRow(record.id, 1, e.target.value)}
              />
            ),
          },
          {
            title: 'Actions',
            key: 'action',
            dataIndex: undefined,
            render: (_val, record) => (
              <Button
                size="small"
                danger
                ghost
                type="default"
                className="types-list-action-button-delete"
                icon={<Icon id="DeleteOutlined" alt="Delete" />}
                onClick={() => onDeleteRow(record.id)}
              />
            ),
          },
        ]}
        footer={() => (
          <Button
            type="default"
            size="small"
            icon={<Icon id="PlusOutlined" alt="Plus" />}
            onClick={onAddRow}
          >
            {language['ui.action.add']}
          </Button>
        )}
      />
    </ConfigProvider>
  );
};

/**
 * Only re-sort when length of list or sort key changes
 */
const useStaticSort = (rows: Row[]) => {
  const [sort, setSort] = useState<{
    ids: number[];
    key: keyof Row;
    order: SortOrder;
  }>(() => ({
    ids: rows.map((f) => f.id),
    key: 'id',
    order: 'ascend',
  }));

  if (rows.length !== sort.ids.length) {
    setSort((prev) => ({ ...prev, ids: sortRows(rows, prev.key, prev.order) }));
  }

  const onTableChange = (
    _pagination: any,
    _filters: any,
    sorter: SorterResult<Row> | SorterResult<Row>[]
  ) => {
    const firstSort = Array.isArray(sorter) ? sorter[0] : sorter;
    const sortKey = (firstSort.columnKey as keyof Row) ?? 'id';
    const order = firstSort.order ?? 'ascend';
    setSort({
      ids: sortRows(rows, sortKey, order),
      key: sortKey,
      order,
    });
  };

  const sortedRows = useMemo(
    () =>
      rows.slice().sort((a, b) => {
        const idxA = sort.ids.indexOf(a.id);
        const idxB = sort.ids.indexOf(b.id);

        return idxA - idxB;
      }),
    [rows, sort]
  );

  return { onTableChange, sortedRows };
};

const sortRows = (rows: Row[], key: keyof Row, order: SortOrder) => {
  return rows
    .slice()
    .sort((a, b) => {
      const sign = order === 'ascend' ? 1 : -1;
      if (key === 'id') {
        return (a.id - b.id) * sign;
      }

      const valA = (a[key] ?? '').toString();
      const valB = (b[key] ?? '').toString();

      return valA.localeCompare(valB) * sign;
    })
    .map((v) => v.id);
};

export default AttributesParsingTable;
